
import React, { memo, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Product } from '@/types/common';
import OptimizedPremiumProductCard from './OptimizedPremiumProductCard';

interface VirtualizedProductGridProps {
  products: Product[];
  itemsPerRow?: number;
  containerHeight?: number;
}

const VirtualizedProductGrid = memo<VirtualizedProductGridProps>(({ 
  products, 
  itemsPerRow = 4,
  containerHeight = 600 
}) => {
  const parentRef = React.useRef<HTMLDivElement>(null);

  // Group products into rows for virtualization
  const rows = useMemo(() => {
    const grouped: Product[][] = [];
    for (let i = 0; i < products.length; i += itemsPerRow) {
      grouped.push(products.slice(i, i + itemsPerRow));
    }
    return grouped;
  }, [products, itemsPerRow]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 400, // Estimated height per row
    overscan: 2, // Render 2 extra items above and below viewport
  });

  const renderRow = useCallback((virtualRow: any) => {
    const row = rows[virtualRow.index];
    if (!row) return null;

    return (
      <div
        key={virtualRow.index}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start}px)`,
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-4">
          {row.map((product) => (
            <OptimizedPremiumProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    );
  }, [rows]);

  return (
    <div ref={parentRef} style={{ height: containerHeight, overflow: 'auto' }}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map(renderRow)}
      </div>
    </div>
  );
});

VirtualizedProductGrid.displayName = 'VirtualizedProductGrid';

export default VirtualizedProductGrid;
